import React from 'react';
import { Box, Typography } from '@mui/material';

const DISPLAY_NAMES = {
    ItemAdded: 'Item added',
    ItemDeleted: 'Item deleted',
    ItemBundled: 'Item bundled',
    ItemUnbundled: 'Item unbundled',
    InvoiceGrouped: 'Invoice grouped',
    InvoiceUngrouped: 'Invoice ungrouped',
    TabAdded: 'Tab added',
    TabDeleted: 'Tab deleted',
    TabRestored: 'Tab restored',
    TabUpdated: 'Tab edit',
    GridEdit: 'Grid edit',
    TemplateSetup: 'Template setup',
    SourceUpdated: 'Price source',
    PublishSettings: 'Publish settings',
    TermsUpdated: 'Terms',
    TemplateGrouping: 'Template grouping',
    ScriptUpdate: 'Script update',
    ContentUpdated: 'Content',
    SpreadsheetCreated: 'Spreadsheet created',
    SpreadsheetSaved: 'Spreadsheet saved'
};

export const parseTemplateChangeDetails = (raw) => {
    if (!raw) {
        return [];
    }

    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return parsed?.Actions || parsed?.actions || [];
    } catch {
        return null;
    }
};

const parseDate = (value) => {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
};

const formatActionLabel = (action) => {
    const type = action?.Type || action?.type;
    return DISPLAY_NAMES[type] || type || 'Update';
};

const formatCountLabel = (action) => {
    const count = action?.Count || action?.count || 1;
    return count === 1 ? '1 time' : `${count} times`;
};

const formatTimeOnly = (value) => {
    const date = parseDate(value);
    if (!date) {
        return null;
    }

    return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit'
    });
};

const changeDetailsGridSx = {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content max-content',
    columnGap: 2.5,
    rowGap: 0.75,
    alignItems: 'baseline',
    width: 'fit-content',
    maxWidth: '100%',
    '& > *:nth-child(-n+3)': {
        pb: 0.5
    }
};

export const QuoteTemplateAuditChangeDetails = ({ row }) => {
    const actions = parseTemplateChangeDetails(row?.ChangeDetails);

    if (!actions || actions.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {row?.EventInfo || 'No additional detail recorded for this entry.'}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: 520 }}>
            <Box sx={changeDetailsGridSx}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Change
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Count
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Last at
                </Typography>
                {actions.flatMap((action, index) => {
                    const lastAtLabel = formatTimeOnly(action?.LastAt || action?.lastAt);

                    return [
                        <Typography
                            key={`change-${action?.Type || action?.type || 'action'}-${index}`}
                            variant="body2"
                            sx={{ color: 'text.primary' }}
                        >
                            {formatActionLabel(action)}
                        </Typography>,
                        <Typography
                            key={`count-${action?.Type || action?.type || 'action'}-${index}`}
                            variant="body2"
                            sx={{ color: 'text.primary' }}
                        >
                            {formatCountLabel(action)}
                        </Typography>,
                        <Typography
                            key={`last-at-${action?.Type || action?.type || 'action'}-${index}`}
                            variant="body2"
                            sx={{ color: 'text.primary' }}
                        >
                            {lastAtLabel || '—'}
                        </Typography>
                    ];
                })}
            </Box>
        </Box>
    );
};
